import { Box, Button, useToast, VStack, useColorModeValue, Spinner } from '@chakra-ui/react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import useAuthAPI from '../../../../api/api_hooks/auth/useAuthAPI'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default function RegisterConfirmed() {
  const toast = useToast()
  const navigate = useNavigate()
  const { useConfirmEmail } = useAuthAPI()
  const confirmEmail = useConfirmEmail()
  const { t } = useTranslation()

  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState<string | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [invitationToken, setInvitationToken] = useState<string | null>(null)

  useEffect(() => {
    setEmail(searchParams.get('email'))
    setToken(searchParams.get('token'))
    setInvitationToken(searchParams.get('invitationToken'))
  }, [searchParams])

  useEffect(() => {
    if (email && token)
      confirmEmail.mutate({ email, token, invitationToken } as unknown as void)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token, invitationToken])

  const formBg = useColorModeValue('white', 'gray.800')
  const formBorder = useColorModeValue('gray.100', 'gray.700')
  const titleColor = useColorModeValue('gray.700', 'gray.200')
  const subTitleColor = useColorModeValue('gray.600', 'gray.300')

  useEffect(() => {
    if (confirmEmail.isError) {
      toast({
        title: t('shared.errors.errorMsg'),
        position: 'top',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }, [confirmEmail.isError, toast, t])

  useEffect(() => {
    if (confirmEmail.isSuccess) {
      toast({
        title: t('auth.confirmEmailSuccess'),
        position: 'top',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    }
  }, [confirmEmail.isSuccess, toast, t])

  // Redirection automatique après confirmation réussie
  useEffect(() => {
    if (confirmEmail.isSuccess) {
      const timer = setTimeout(() => navigate('/login'), 3000)
      return () => clearTimeout(timer)
    }
  }, [confirmEmail.isSuccess, navigate])

  return (
    <Box w="100%" maxW={420} mx="auto">
      <VStack spacing={4} w="100%">

        {/* Header — masqué si confirmation réussie */}
        {!confirmEmail.isSuccess && (
          <Box textAlign="center" w="100%">
            <Box fontSize={{ base: 'xl', md: '2xl' }} fontWeight="600" color={titleColor} mb={2}>
              {t('auth.welcomeCareflai')}
            </Box>
            {!confirmEmail.isError && (
              <Box fontSize={{ base: 'sm', md: 'md' }} color={subTitleColor}>
                {t('auth.confirmEmailSubtitle')}
              </Box>
            )}
          </Box>
        )}

        <Box
          w="100%"
          bg={formBg}
          p={{ base: 8, md: 10 }}
          borderRadius="xl"
          boxShadow="xl"
          borderWidth="1px"
          borderColor={formBorder}
          textAlign="center"
        >
          {!email || !token ? (
            <VStack spacing={4}>
              <Box fontSize="4xl">⚠️</Box>
              <Box fontWeight="600" color={titleColor}>Lien de confirmation invalide</Box>
              <Box fontSize="sm" color={subTitleColor}>
                Veuillez cliquer sur le lien dans votre email de confirmation.
              </Box>
              <Button w="100%" colorScheme="purple" size="lg" fontWeight={600} borderRadius="xl" mt={4}
                onClick={() => navigate('/login')}
                _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }} transition="all 0.2s">
                Retour à la connexion
              </Button>
            </VStack>
          ) : (
            <>
              {confirmEmail.isPending && (
                <VStack spacing={4}>
                  <Spinner size="xl" color="purple.500" thickness="4px" />
                  <Box color={subTitleColor}>Confirmation en cours…</Box>
                </VStack>
              )}

              {confirmEmail.isSuccess && (
                <VStack spacing={6}>
                  <Box fontSize="5xl">✅</Box>
                  <VStack spacing={1}>
                    <Box fontWeight="700" fontSize="lg" color={titleColor}>
                      Email confirmé !
                    </Box>
                    <Box fontSize="sm" color={subTitleColor}>
                      Redirection vers la connexion dans 3 secondes…
                    </Box>
                  </VStack>
                  <Button w="100%" colorScheme="purple" size="lg" fontWeight={600} borderRadius="xl"
                    onClick={() => navigate('/login')}
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }} transition="all 0.2s">
                    Se connecter maintenant
                  </Button>
                </VStack>
              )}

              {confirmEmail.isError && (
                <VStack spacing={4}>
                  <Box fontSize="4xl">❌</Box>
                  <Box fontWeight="600" color="red.500">Erreur de confirmation</Box>
                  <Box fontSize="sm" color={subTitleColor}>
                    Le lien est peut-être expiré ou déjà utilisé.
                  </Box>
                  <Button w="100%" colorScheme="purple" size="lg" fontWeight={600} borderRadius="xl" mt={2}
                    onClick={() => navigate('/login')}
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'xl' }} transition="all 0.2s">
                    Retour à la connexion
                  </Button>
                </VStack>
              )}
            </>
          )}
        </Box>
      </VStack>
    </Box>
  )
}
